import i18n from "lib/i18n"
import PropTypes from "prop-types"
import React, { useState } from "react"

import SubmitForm from "./SubmitForm"
import ToggleLockButton from "../../../components/Editorial/ToggleLockUser/Button"
import ToggleLockDialog from "../../../components/Editorial/ToggleLockUser/Dialog"

/* eslint-disable max-len */
/**
 * Component that will render a button to lock/unlock users. Clicking
 * on the button will then show a dialog to input the lock/unlock reason.
 *
 * You can opt out from showing the button and trigger the dialog manually by:
 * => passing `false` to the `showButton` prop
 * => controlling the dialog opened state through the `dialogOpen` prop
 *
 * @param {Function} [afterSubmit] callback function executed after the lock/unlock submit request
 * @param {String} [buttonClass] class name attribute for the button
 * @param {Function} [closeDialogCallback] callback function when the dialog closes. Useful when you want to control state
 * @param {Boolean} locked user is locked or not
 * @param {Boolean} [dialogOpen=false] determines if the dialog is opened
 * @param {Boolean} [showButton=true] shows or hide action button
 * @param {Number, String} userId the user ID
 * @param {String} userName the username
 */
/* eslint-enable max-len */
export default function ToggleLockUser({
  afterSubmit = () => {},
  buttonClass = "",
  closeDialogCallback = () => {},
  dialogOpen = false,
  locked,
  showButton = true,
  userId,
  userName,
}) {
  const [userLocked, setUserLocked] = useState(locked)
  const [internalModalOpen, setInternalModalOpen] = useState(false)

  const modalOpen = showButton ? internalModalOpen : dialogOpen

  const submitForm = (reason) => {
    new SubmitForm({
      locked: userLocked,
      reason,
      userId,
    }).submit((isLocked) => {
      afterSubmit(isLocked)
      setUserLocked(isLocked)
      closeDialogCallback()
      if (showButton) {
        setInternalModalOpen(false)
      }
    })
  }

  const toggleLockText = userLocked
    ? i18n.t("editorial_toggle_lock_user.action.unlock")
    : i18n.t("editorial_toggle_lock_user.action.lock")

  return (
    <>
      {showButton ? (
        <ToggleLockButton
          className={buttonClass}
          locked={userLocked}
          onClick={() => {
            setInternalModalOpen(true)
          }}
        >
          {toggleLockText}
        </ToggleLockButton>
      ) : null}

      <ToggleLockDialog
        locked={userLocked}
        onCancel={() => {
          closeDialogCallback()
          if (showButton) {
            setInternalModalOpen(false)
          }
        }}
        onSubmit={submitForm}
        open={modalOpen}
        userId={userId}
        userName={userName}
      />
    </>
  )
}

ToggleLockUser.propTypes = {
  afterSubmit: PropTypes.func,
  buttonClass: PropTypes.string,
  closeDialogCallback: PropTypes.func,
  dialogOpen: PropTypes.bool,
  locked: PropTypes.bool.isRequired,
  showButton: PropTypes.bool,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  userName: PropTypes.string.isRequired,
}
